import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, Calendar, Tag, ChevronRight } from 'lucide-react';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = window.location.origin;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => { setPage(1); }, [debouncedSearch, activeCategory]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 9 });
      if (activeCategory) params.set('category', activeCategory);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await axios.get(`${API}/blog?${params}`);
      if (res.data.items) {
        setPosts(res.data.items);
        setTotalPages(res.data.pages);
      } else {
        setPosts(Array.isArray(res.data) ? res.data : []);
        setTotalPages(1);
      }
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [page, activeCategory, debouncedSearch]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  useEffect(() => {
    axios.get(`${API}/blog/categories`).then(res => setCategories(res.data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen pt-28 sm:pt-36 pb-16 sm:pb-24" data-testid="blog-page">
      <SEO
        title="Blog - Gangadhar Som | Art, Technique & Exhibition Stories"
        description="Read articles about art technique, devotional painting traditions, exhibition stories, and the creative journey of renowned Indian artist Gangadhar Som."
        keywords="Gangadhar Som blog, art technique, devotional painting, Indian art blog, exhibition stories, painting tutorials, artist journal"
        url={`${BASE_URL}/blog`}
        type="website"
        schema={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Gangadhar Som - News & Stories",
          "description": "Thoughts on art, technique, exhibitions, and the creative journey",
          "url": `${BASE_URL}/blog`,
          "author": { "@type": "Person", "name": "Gangadhar Som", "@id": `${BASE_URL}/#artist` },
          "publisher": { "@type": "Person", "name": "Gangadhar Som" },
        }}
      />
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-4" data-testid="blog-title">
            News & Stories
          </h1>
          <p className="font-sans text-base text-muted-foreground max-w-2xl mx-auto">
            Thoughts on art, technique, exhibitions, and the creative journey
          </p>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-10 justify-center">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              data-testid="blog-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setActiveCategory('')}
              className={`px-3.5 py-1.5 rounded-full text-xs font-sans font-medium transition-colors ${!activeCategory ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              data-testid="blog-filter-all"
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-sans font-medium transition-colors ${activeCategory === cat ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                data-testid={`blog-filter-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Posts Grid */}
        {loading ? (
          <p className="text-center text-muted-foreground py-16 font-sans">Loading articles...</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-20" data-testid="no-posts-message">
            <p className="font-serif text-2xl font-light mb-2">No articles found</p>
            <p className="text-muted-foreground font-sans text-sm">
              {debouncedSearch || activeCategory ? 'Try adjusting your search or filters.' : 'Articles will appear here once published.'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, idx) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                data-testid={`blog-post-card-${post.slug}`}
              >
                <Link to={`/blog/${post.slug}`} className="group block">
                  {post.cover_image ? (
                    <div className="aspect-[16/10] rounded-lg overflow-hidden mb-4 bg-muted">
                      <img
                        src={post.cover_medium || post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] rounded-lg overflow-hidden mb-4 bg-muted/40 flex items-center justify-center">
                      <span className="font-serif text-3xl text-muted-foreground/30">{post.title.charAt(0)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-accent font-medium">{post.category}</span>
                    <span className="flex items-center gap-1 text-[10px] font-sans text-muted-foreground">
                      <Calendar size={10} />
                      {formatDate(post.published_at || post.created_at)}
                    </span>
                  </div>
                  <h2 className="font-serif text-xl font-light mb-2 group-hover:text-accent transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="font-sans text-sm text-muted-foreground line-clamp-3 mb-3">{post.excerpt}</p>
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {post.tags.slice(0, 4).map((tag, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 bg-accent/10 text-accent rounded-full font-sans flex items-center gap-0.5">
                          <Tag size={8} />{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs font-sans font-medium text-accent group-hover:gap-2 transition-all">
                    Read more <ChevronRight size={12} />
                  </span>
                </Link>
              </motion.article>
            ))}
          </div>
        )}

        {/* Load More */}
        {totalPages > 1 && page < totalPages && (
          <div className="text-center mt-12">
            <button
              onClick={() => setPage(p => p + 1)}
              className="px-8 py-3 border border-accent text-accent rounded-full font-sans text-sm hover:bg-accent hover:text-white transition-colors"
              data-testid="blog-load-more"
            >
              Load More Articles
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
